import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {graphqlClient} from '@/services/graphqlClient';
import type {Room} from '@/services/graphqlHooks';
import {
  useGetAllCurrentResidentsQuery,
  useGetRoomsQuery,
} from '@/services/graphqlHooks';

import type {ResidentWithOccupiedRoom} from '../utils/getResidentsWithRoom';
import {getResidentWithOccupiedRoom} from '../utils/getResidentsWithRoom';
import ExportResidentsTable from './ExportResidentsTable';

export type ListResidentsInfoDialogProps = DialogProps;

const ExportResidentsDialog = ({
  onOpenChange,
  ...props
}: ListResidentsInfoDialogProps) => {
  const {t} = useTranslation();
  const {data: residentsData, dataUpdatedAt: residentsDataUpdatedAt} =
    useGetAllCurrentResidentsQuery(graphqlClient, undefined, {
      enabled: props.open,
    });

  const {data: roomsData, dataUpdatedAt: roomsDataUpdatedAt} = useGetRoomsQuery(
    graphqlClient,
    undefined,
    {
      enabled: props.open,
    }
  );

  const residents = (residentsData?.getAllCurrentResidents ||
    []) as ResidentWithOccupiedRoom[];
  const rooms = (roomsData?.getRooms || []) as Room[];

  const residentsWithOccupiedRoom = useMemo(
    () =>
      residents.map(resident => getResidentWithOccupiedRoom(resident, rooms)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomsDataUpdatedAt, residentsDataUpdatedAt]
  );

  return (
    <Dialog {...props} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t('Export residents')}</DialogTitle>
        </DialogHeader>
        <DialogSection>
          <ExportResidentsTable residents={residentsWithOccupiedRoom} />
        </DialogSection>
        <DialogFooter>
          <Button
            type="button"
            variant="primary"
            onClick={() => onOpenChange?.(false)}
          >
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportResidentsDialog;
